// NEW_FILE_JUSTIFIED: Step 1 of claim wizard — policy selection, extracted to stay under 200 lines
import { useEffect, useRef } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ require three levels up to reach convex/_generated/
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldCheck, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern

// LOCAL_TYPE_JUSTIFIED: step-internal props, not reused outside the wizard
interface IClaimWizardStepPolicyProps {
  orgId: Id<'organisations'>;
  selectedPolicyId: string;
  onSelect: (policyId: string) => void;
  onNext: () => void;
  /** Called once when a template is found for the selected policy's insurance type */
  onTemplateAutoSelect?: (templateId: Id<'claimFormTemplates'>) => void;
}

function formatDate(ts: number | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString();
}

export function ClaimWizardStepPolicy({
  orgId,
  selectedPolicyId,
  onSelect,
  onNext,
  onTemplateAutoSelect,
}: IClaimWizardStepPolicyProps) {
  // Use listForPortalUser — works for both org members (via leads) and company members (via employeeRecordId)
  const allPolicies = useQuery(api.insurance.policies.listForPortalUser);

  // Fetch full policy detail to get insuranceTypeId for template auto-selection
  const selectedPolicyDetail = useQuery(
    api['insurance/policies'].get,
    selectedPolicyId
      ? { policyId: selectedPolicyId as Id<'insurancePolicies'> } // AS_JUSTIFIED: selectedPolicyId originates from listForPortalUser policy _id, safe to narrow to typed Id
      : 'skip'
  );

  // Fetch templates matching the selected policy's insurance type
  const policyOrgId = selectedPolicyDetail?.orgId ?? orgId;
  const insuranceTypeId = selectedPolicyDetail?.insuranceTypeId;
  const matchingTemplates = useQuery(
    api.insurance.claimFormTemplates.list,
    insuranceTypeId ? { orgId: policyOrgId, insuranceTypeId } : 'skip'
  );

  // Sentinel: only call onTemplateAutoSelect once per selected template
  const notifiedTemplateRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onTemplateAutoSelect) return;
    const tmpl = matchingTemplates?.[0];
    if (!tmpl) return;
    if (notifiedTemplateRef.current === tmpl._id) return;
    notifiedTemplateRef.current = tmpl._id;
    onTemplateAutoSelect(tmpl._id);
  }, [matchingTemplates, onTemplateAutoSelect]);

  const policies = allPolicies?.filter(p => p.status === 'active');

  if (policies === undefined) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (policies.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <ShieldCheck className="mx-auto mb-3 h-10 w-10 opacity-40" />
        <p className="font-medium">No active policies found</p>
        <p className="text-sm">You need an active insurance policy to submit a claim.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Select the policy this claim is for.</p>
      <div className="space-y-3">
        {policies.map(policy => (
          <button
            key={policy._id}
            type="button"
            data-testid={`policy-card-${policy.policyNumber}`}
            onClick={() => onSelect(policy._id)}
            className={cn(
              'w-full rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
              selectedPolicyId === policy._id
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-border'
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-1">
                <p className="font-medium">{policy.policyNumber}</p>
                {policy.insuranceTypeName && (
                  <p className="text-sm text-muted-foreground">{policy.insuranceTypeName}</p>
                )}
                {policy.expiresAt && (
                  <p className="text-xs text-muted-foreground">
                    Expires: {formatDate(policy.expiresAt)}
                  </p>
                )}
              </div>
              <Badge variant="secondary">{policy.status}</Badge>
            </div>
          </button>
        ))}
      </div>
      <div className="flex justify-end pt-2">
        <Button onClick={onNext} disabled={!selectedPolicyId} data-testid="claim-wizard-next">
          Next
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
